import { IApp, IProjectApp } from "@mctypes/global.types";
import { Alert, Card, Paper, Stack, Table, Text, Title } from "@mantine/core";
import * as R from "ramda";
import React from "react";
import { Checkbox } from "../../../components/";
import { selectUserMember, setShowSuccessMessageThunk } from "../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { ToastMessagesEnums } from "../../../mc-shared/sharedConstants";
import { setProjectAppsInRedux } from "../../actions";
import { doSaveProjectApps } from "./restQueries";

const EnabledApps = () => {
  const apps = useAppSelector((state) => state.globalReducer.apps);
  const project = useAppSelector((state) => state.adminReducer.project);
  const userMember = useAppSelector(selectUserMember);
  const userIsAdmin = userMember?.group === "ADMIN";

  const dispatch = useAppDispatch();

  const updateProjectApp = async (projectApp: IProjectApp, path: string[], bool: boolean) => {
    const index = project.apps.findIndex((_prjApp) => _prjApp.app === projectApp.app);
    const copyOfProjectApps = [...project.apps];
    copyOfProjectApps[index] = R.assocPath(path, bool, copyOfProjectApps[index]);
    const savedProjectApps = await doSaveProjectApps(project._id, copyOfProjectApps);
    dispatch(setProjectAppsInRedux(savedProjectApps));
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
  };

  const visibleApps = apps.filter((app) => {
    if (app.enabledInProjects == null) {
      return true;
    }

    return app.enabledInProjects.indexOf(project.maconomy.projectNo) !== -1;
  });

  return (
    <Card withBorder p="md">
      <Stack gap="sm">
        <Title order={4}>Aktiverte applikasjoner</Title>
        <Text size="sm" c="dimmed">
          Velg hvilke applikasjoner og hvilke som skal ha tilgang
        </Text>
        {userIsAdmin === false && (
          <Alert color="blue" variant="light">
            Administreres av prosjektadministratorer
          </Alert>
        )}
        <Table
          verticalSpacing="xs"
          withTableBorder={false}
          withColumnBorders={false}
          styles={(theme) => ({
            th: {
              fontWeight: 500,
              color: theme.colors.gray[7],
            },
          })}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: "25%" }}>App</Table.Th>
              <Table.Th style={{ width: "25%" }}>Intern</Table.Th>
              <Table.Th style={{ width: "25%" }}>Partner</Table.Th>
              <Table.Th style={{ width: "25%" }}>Kunde</Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {visibleApps.map((app) => (
              <AppRow
                key={app.name}
                isAdmin={userIsAdmin}
                app={app}
                projectApp={project.apps.find((prjApp) => prjApp.app === app.name)}
                updateProjectApp={updateProjectApp}
              />
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Card>
  );
};

export default EnabledApps;

const AppRow: React.FC<{
  isAdmin: boolean;
  projectApp: IProjectApp;
  app: IApp;
  updateProjectApp: (projectApp: IProjectApp, path: string[], bool: boolean) => void;
}> = ({ isAdmin, projectApp, app, updateProjectApp }) => {
  if (projectApp == null) {
    return null;
  }

  return (
    <Table.Tr>
      <Table.Td>{app.label}</Table.Td>
      <Table.Td style={{ textAlign: "center" }}>
        <Checkbox
          isChecked={projectApp.active}
          setChecked={() => updateProjectApp(projectApp, ["active"], !projectApp.active)}
          disabled={isAdmin === false || (projectApp.app === "checklist" && projectApp.active === true)}
        />
      </Table.Td>
      <Table.Td style={{ textAlign: "center" }}>
        <Checkbox
          isChecked={projectApp.access.partner}
          setChecked={() => updateProjectApp(projectApp, ["access", "partner"], !projectApp.access.partner)}
          disabled={isAdmin === false || projectApp.active === false}
        />
      </Table.Td>
      <Table.Td style={{ textAlign: "center" }}>
        <Checkbox
          isChecked={projectApp.access.client}
          setChecked={() => updateProjectApp(projectApp, ["access", "client"], !projectApp.access.client)}
          disabled={isAdmin === false || projectApp.active === false}
        />
      </Table.Td>
    </Table.Tr>
  );
};
